import React from "react";
import { StyleSheet, Text, View } from "react-native";
import { AntDesign, Ionicons } from "@expo/vector-icons";

import Colors from "../../../../Resources/Color";
import HoverableTouchableOpacity from "../../../Components/HoverableTouchableOpacity";
import {
  InlineNutritionSummary,
  QuantityControl,
  useResponsiveIngredientRowLayout,
} from "./FoodRowShared";

function getEntityTone(entityKind) {
  if (entityKind === "equivalent") {
    return {
      accent: Colors.fatsColor,
      icon: "git-compare-outline",
      backgroundColor: "rgba(221, 110, 82, 0.08)",
      hoverBackgroundColor: "rgba(221, 110, 82, 0.06)",
      hoverBorderColor: "rgba(221, 110, 82, 0.3)",
    };
  }

  if (entityKind === "recipe") {
    return {
      accent: Colors.proteinColor,
      icon: "restaurant-outline",
      backgroundColor: "rgba(74, 157, 114, 0.1)",
      hoverBackgroundColor: "rgba(74, 157, 114, 0.07)",
      hoverBorderColor: "rgba(74, 157, 114, 0.3)",
    };
  }

  return {
    accent: Colors.bubbleColor,
    icon: null,
    backgroundColor: "transparent",
    hoverBackgroundColor: "rgba(63, 123, 141, 0.025)",
    hoverBorderColor: "rgba(63, 123, 141, 0.5)",
  };
}

export function FoodBrowserResultRow({
  rowRef,
  isInline = false,
  isHighlighted = false,
  compactLayout = false,
  canSelect = true,
  onPress,
  onHover,
  quantityValue,
  onChangeQuantity,
  onSubmitQuantity,
  unitOptions,
  selectedUnitKey,
  onSelectUnit,
  referenceText,
  title,
  subtitle,
  entityKind = "food",
  entityLabel = null,
  previewText = "",
  totals,
  showQuantityControl = true,
  showNutritionSummary = true,
  nutritionSummaryLabel = null,
}) {
  const { handleRowLayout, shouldUseCompactLayout } =
    useResponsiveIngredientRowLayout(compactLayout);
  const entityTone = getEntityTone(entityKind);
  const hasEntityChrome = entityKind !== "food";

  return (
    <HoverableTouchableOpacity
      ref={rowRef}
      style={[
        styles.browserResultRow,
        isInline ? styles.browserResultRowInline : null,
      ]}
      activeOpacity={0.85}
      onPress={() => {
        if (canSelect) {
          onPress?.();
        }
      }}
      onMouseEnter={onHover}
      disabled={!canSelect}
    >
      {() => (
        <View
          onLayout={handleRowLayout}
          style={[
            styles.browserResultRowSurface,
            isInline ? styles.browserResultRowSurfaceInline : null,
            isHighlighted && canSelect
              ? {
                  backgroundColor: entityTone.hoverBackgroundColor,
                  borderTopColor: entityTone.hoverBorderColor,
                  borderBottomColor: entityTone.hoverBorderColor,
                }
              : null,
          ]}
        >
          <View
            style={[
              styles.browserResultShellRow,
              shouldUseCompactLayout ? styles.browserResultShellRowCompact : null,
            ]}
          >
            <View style={styles.browserResultLeadingRail}>
              <View
                style={[
                  styles.addIngredientButton,
                  hasEntityChrome
                    ? {
                        backgroundColor: entityTone.backgroundColor,
                        borderWidth: 1,
                        borderColor: "rgba(63, 123, 141, 0.08)",
                      }
                    : null,
                  !canSelect ? styles.addIngredientButtonDisabled : null,
                ]}
              >
                <View
                  style={[
                    styles.addIngredientIconWrap,
                    isHighlighted && canSelect
                      ? styles.addIngredientIconWrapHovered
                      : null,
                  ]}
                >
                  {hasEntityChrome ? (
                    <Ionicons
                      name={entityTone.icon}
                      size={15}
                      color={entityTone.accent}
                    />
                  ) : (
                    <AntDesign
                      name="plus"
                      size={16}
                      style={[
                        styles.addIngredientIcon,
                        isHighlighted && canSelect
                          ? styles.addIngredientIconHovered
                          : null,
                      ]}
                    />
                  )}
                </View>
              </View>
            </View>

            <View style={styles.browserResultContentColumn}>
              <View
                style={[
                  styles.selectedIngredientMainRow,
                  shouldUseCompactLayout
                    ? styles.selectedIngredientMainRowCompact
                    : null,
                ]}
              >
                <View
                  style={[
                    styles.selectedIngredientIdentity,
                    shouldUseCompactLayout
                      ? styles.selectedIngredientIdentityCompact
                      : styles.selectedIngredientIdentityDesktop,
                  ]}
                >
                  {showQuantityControl ? (
                    <View
                      style={styles.browserResultQuantityGuard}
                      onMouseDown={(event) => {
                        event.stopPropagation?.();
                      }}
                      onClick={(event) => {
                        event.stopPropagation?.();
                      }}
                      onTouchStart={(event) => {
                        event.stopPropagation?.();
                      }}
                    >
                      <QuantityControl
                        value={quantityValue}
                        onChangeText={onChangeQuantity}
                        onSubmitEditing={onSubmitQuantity}
                        unitOptions={unitOptions}
                        selectedUnitKey={selectedUnitKey}
                        onSelectUnit={onSelectUnit}
                        referenceText={referenceText}
                      />
                    </View>
                  ) : null}
                  <View
                    style={[
                      styles.ingredientInfo,
                      shouldUseCompactLayout ? styles.ingredientInfoCompact : null,
                    ]}
                  >
                    {entityLabel ? (
                      <Text
                        style={[
                          styles.entityLabel,
                          {
                            color: entityTone.accent,
                          },
                        ]}
                      >
                        {entityLabel}
                      </Text>
                    ) : null}
                    <Text numberOfLines={2} style={styles.ingredientName}>
                      {title}
                    </Text>
                    {subtitle ? (
                      <Text numberOfLines={1} style={styles.ingredientSource}>
                        {subtitle}
                      </Text>
                    ) : null}
                    {previewText ? (
                      <Text numberOfLines={1} style={styles.ingredientPreview}>
                        {previewText}
                      </Text>
                    ) : null}
                  </View>
                </View>

                <View
                  style={[
                    styles.selectedIngredientUtilityRow,
                    shouldUseCompactLayout
                      ? styles.selectedIngredientUtilityRowCompact
                      : null,
                  ]}
                >
                  {showNutritionSummary ? (
                    <View
                      style={[
                        styles.selectedIngredientNutrition,
                        shouldUseCompactLayout
                          ? styles.selectedIngredientNutritionCompact
                          : null,
                      ]}
                    >
                      {nutritionSummaryLabel ? (
                        <Text style={styles.nutritionSummaryLabel}>
                          {nutritionSummaryLabel}
                        </Text>
                      ) : null}
                      <InlineNutritionSummary
                        calories={totals?.calories}
                        fats={totals?.fats}
                        carbs={totals?.carbs}
                        protein={totals?.protein}
                      />
                    </View>
                  ) : null}
                </View>
              </View>
            </View>
          </View>
        </View>
      )}
    </HoverableTouchableOpacity>
  );
}

const styles = StyleSheet.create({
  browserResultRow: {
    marginHorizontal: -16,
  },
  browserResultRowInline: {
    marginHorizontal: -14,
  },
  browserResultRowSurface: {
    paddingVertical: 10,
    paddingHorizontal: 16,
    backgroundColor: "transparent",
    borderTopWidth: 1,
    borderBottomWidth: 1,
    borderTopColor: "transparent",
    borderBottomColor: "transparent",
    transitionProperty:
      "background-color, border-top-color, border-bottom-color",
    transitionDuration: "160ms",
    transitionTimingFunction: "ease",
  },
  browserResultRowSurfaceInline: {
    paddingHorizontal: 14,
  },
  browserResultShellRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 10,
    minWidth: 0,
  },
  browserResultShellRowCompact: {
    alignItems: "flex-start",
  },
  browserResultLeadingRail: {
    width: 32,
    flexShrink: 0,
    alignItems: "center",
    justifyContent: "center",
    alignSelf: "center",
  },
  browserResultContentColumn: {
    flex: 1,
    minWidth: 0,
  },
  selectedIngredientMainRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 10,
    flexWrap: "nowrap",
  },
  selectedIngredientMainRowCompact: {
    alignItems: "flex-start",
    flexWrap: "wrap",
    rowGap: 8,
  },
  selectedIngredientIdentity: {
    flexGrow: 1,
    flexShrink: 1,
    flexBasis: 320,
    minWidth: 0,
    flexDirection: "row",
    alignItems: "center",
    gap: 10,
  },
  selectedIngredientIdentityDesktop: {
    flexWrap: "nowrap",
  },
  selectedIngredientIdentityCompact: {
    flex: 1,
    minWidth: 0,
    width: "auto",
    flexWrap: "nowrap",
  },
  browserResultQuantityGuard: {
    paddingVertical: 8,
    marginVertical: -8,
    cursor: "default",
    flexShrink: 0,
  },
  ingredientInfo: {
    flex: 1,
    minWidth: 0,
  },
  ingredientInfoCompact: {
    flex: 1,
    minWidth: 0,
  },
  ingredientName: {
    fontSize: 13,
    lineHeight: 18,
    fontWeight: "500",
    color: Colors.blackColor,
  },
  entityLabel: {
    marginBottom: 2,
    fontSize: 10,
    lineHeight: 12,
    fontWeight: "600",
    textTransform: "uppercase",
    letterSpacing: 0.5,
  },
  ingredientSource: {
    marginTop: 3,
    fontSize: 10,
    lineHeight: 12,
    color: Colors.grayColor,
    textTransform: "uppercase",
    letterSpacing: 0.45,
  },
  ingredientPreview: {
    marginTop: 3,
    fontSize: 11,
    lineHeight: 14,
    color: Colors.darkgrayColor,
  },
  selectedIngredientUtilityRow: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "flex-end",
    gap: 10,
    marginLeft: "auto",
    maxWidth: "100%",
    minWidth: 0,
    flexShrink: 1,
  },
  selectedIngredientUtilityRowCompact: {
    width: "100%",
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 12,
    marginTop: 0,
  },
  selectedIngredientNutrition: {
    minWidth: 0,
    flexShrink: 1,
    alignItems: "flex-end",
  },
  selectedIngredientNutritionCompact: {
    flex: 1,
    minWidth: 0,
    alignItems: "flex-start",
  },
  nutritionSummaryLabel: {
    fontSize: 10,
    lineHeight: 13,
    fontWeight: "400",
    color: Colors.grayColor,
    marginBottom: 4,
  },
  addIngredientButton: {
    width: 32,
    height: 32,
    borderRadius: 16,
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "transparent",
    flexShrink: 0,
  },
  addIngredientButtonDisabled: {
    opacity: 0.45,
  },
  addIngredientIconWrap: {
    alignItems: "center",
    justifyContent: "center",
    transform: [{ scale: 1 }],
    transitionProperty: "transform",
    transitionDuration: "160ms",
    transitionTimingFunction: "ease",
  },
  addIngredientIconWrapHovered: {
    transform: [{ scale: 1.12 }],
  },
  addIngredientIcon: {
    color: Colors.grayColor,
    transitionProperty: "color",
    transitionDuration: "160ms",
    transitionTimingFunction: "ease",
  },
  addIngredientIconHovered: {
    color: Colors.bubbleColor,
  },
});
